import { useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "components/AlertDialog";
import Button from "components/Button";
import { Form } from "components/Form";
import StepperIndicator from "components/StepperIndicator";

import ConsentStep from "./ConsentStep";
import PersonalDetailsStep from "./PersonalDetailsStep";
import { buildCompanySettingsSchema } from "./schema";
import TemplateSelectionStep from "./TemplateSelectionStep";
import { useCompanySettingsDialog } from "./useCompanySettingsDialog";

interface CompanySettingsInsertRequest {
  languageId: string;
  templateId: string;
  userUpdateRequest: {
    firstName: string;
    lastName: string;
    phone: string;
  };
  companyName: string;
}

export default function CompanySettingsDialog({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    { title: t("settings.companySettingsDialog.details"), value: "details" },
    { title: t("settings.companySettingsDialog.conditions"), value: "conditions" },
    { title: t("settings.companySettingsDialog.findings"), value: "findings" },
  ];

  const companySettingsSchema = buildCompanySettingsSchema(t);

  const { useCreateCompanySettingsMutation, getRegisteredUserQuery, languages } = useCompanySettingsDialog();

  const { mutate } = useCreateCompanySettingsMutation(setIsOpen);

  const uiLocale = localStorage.getItem("ui_locales");
  const selectedLang = languages.find((lang) => lang.code === uiLocale);

  const form = useForm<z.infer<typeof companySettingsSchema>>({
    resolver: zodResolver(companySettingsSchema),
    defaultValues: {
      companyName: undefined,
      firstName: undefined,
      lastName: undefined,
      phone: undefined,
      confirmation: false,
      languageId: selectedLang ? selectedLang.value : "1",
      templateId: "1",
    },
  });

  const { watch, handleSubmit, trigger } = form;
  const confirmation = watch("confirmation");

  const getCurrentStepFields = (): Array<
    "firstName" | "lastName" | "companyName" | "phone" | "languageId" | "confirmation" | "templateId"
  > => {
    switch (currentStep) {
      case 0:
        return ["firstName", "lastName", "companyName", "phone", "languageId"];
      case 1:
        return ["confirmation"];
      case 2:
        return ["templateId"];
      default:
        return [];
    }
  };

  const nextStep = async () => {
    const currentStepFields = getCurrentStepFields();

    const isStepValid = await trigger(currentStepFields);

    if (isStepValid) {
      setCurrentStep(currentStep + 1);
    } else {
      console.log("Validation failed for step", currentStep);
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const onSubmit = (values: z.infer<typeof companySettingsSchema>) => {
    const request: CompanySettingsInsertRequest = {
      languageId: values.languageId,
      templateId: values.templateId,
      userUpdateRequest: {
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
      },
      companyName: values.companyName,
    };

    mutate(request);
  };

  useEffect(() => {
    if (getRegisteredUserQuery.data) {
      form.reset({ ...getRegisteredUserQuery.data, languageId: selectedLang ? selectedLang.value : "1", templateId: "1" });
    }
  }, [form, getRegisteredUserQuery.data]);

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return <PersonalDetailsStep languages={languages} />;
      case 1:
        return <ConsentStep />;
      case 2:
        return <TemplateSelectionStep />;
      default:
        return null;
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogContent className="max-w-md focus:outline-none">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-center text-xl font-semibold">
            {t("settings.companySettingsDialog.title")}
          </AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogDescription className="hidden">{t("settings.companySettingsDialog.title")}</AlertDialogDescription>
        <StepperIndicator activeStep={currentStep + 1} steps={steps} />
        <Form {...form}>
          <div className="space-y-4 h-[480px] overflow-y-auto">{renderCurrentStep()}</div>
        </Form>
        <AlertDialogFooter className="flex justify-between">
          {currentStep > 0 && (
            <Button type="button" variant="secondary" onClick={prevStep} className="w-full md:flex-1">
              {t("button.back")}
            </Button>
          )}

          {currentStep === 2 ? (
            <Button type="button" onClick={handleSubmit(onSubmit)} className="w-full md:flex-1">
              {t("button.submit")}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={nextStep}
              disabled={(currentStep === 1 && !confirmation) || (currentStep === 2 && !watch("templateId"))}
              className="w-full md:flex-1"
            >
              {t("button.next")}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
